import moment from 'moment'
import React, { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'

import { Box, Button, Flex, Link } from '@chakra-ui/react'
import { useFormContext, useWatch } from 'react-hook-form'

import { Card, LoadingSection } from '@/components/Elements'

import { TooltipCell } from '@/components/Table/components/Cell/ToolTipCell'

import useOffer from '@/features/offers/hooks/useOffer'
import usePrintCaptureMode from '@/features/offers/hooks/usePrintCaptureMode'
import { setFileHistoryModal } from '@/stores/ui/uiSlice'
import { camelToWords, round } from '@/utils/stringHelper'
import getOfferHistoryTableColumns from './OfferHistoryTableColumns'
import EditableTable from '@/components/Table/EditableTable'

//API

const HistoryForm = () => {
  const dispatch = useDispatch()
  const { getValues } = useFormContext()

  const id = useWatch({ name: '_id' })
  const isPrintCaptureMode = usePrintCaptureMode()

  //TODO
  const { useGetAllOfferRevisions } = useOffer()
  const { data: allRevisionResponse, isLoading: isLoadingRevisions } = useGetAllOfferRevisions(id)

  const handleOpenFileDetail = useCallback(
    (files, title) => {
      dispatch(
        setFileHistoryModal({
          show: true,
          title: title,
          files: files || [],
        })
      )
    },
    [dispatch]
  )

  const history = useWatch({ name: 'history' }) ?? []
  const offerHistoryTableData = history
    .map((item) => {
      return {
        ...item,
        name: item.position.toLowerCase() === 'system' ? '' : item.user?.fullName,
      }
    })
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .reduce((result, item) => {
      const groupedRow = result.find((res) => res.revision === item.revision)
      if (!groupedRow) {
        result.push({
          revision: item.revision,
          subRows: [item],
        })
      } else {
        groupedRow.subRows.push(item)
      }
      return result
    }, [])
    .sort((a, b) => b.revision - a.revision)

  const columnDef = getOfferHistoryTableColumns({ handleOpenFileDetail })

  if (!history) {
    return <LoadingSection />
  }

  return (
    <>
      {!isPrintCaptureMode && (
        <Card title={'Revise Detail'} hasBorder={false}>
          <Flex gap={3} data-print-loading={isLoadingRevisions ? 'true' : undefined}>
            {allRevisionResponse?.allRevisions &&
              allRevisionResponse.allRevisions.map((revision) => (
                <Button
                  key={revision.version}
                  as={Link}
                  bg={revision.version === 0 ? 'blue.400' : 'green.400'}
                  color="white"
                  disabled={revision.offerId === id}
                  href={`/offers/${revision.offerId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  R{revision.version}
                </Button>
              ))}
          </Flex>
        </Card>
      )}
      <Box p={5} overflowX={isPrintCaptureMode ? 'visible' : 'scroll'}>
        <EditableTable
          columnDef={columnDef}
          data={offerHistoryTableData}
          disabled
          tableWidth="100%"
          grouping={['revision']}
          textAlign="left"
          // Revision groups are collapsed by default; expand them all so print capture doesn't
          // silently drop history entries that were hidden behind a collapsed row. Uses `state`
          // (reactively re-applied every render) rather than `initialState` (applied once, at
          // mount only) — if the History tab happens to already be mounted/active when print
          // capture starts, this component doesn't remount, so initialState would never re-fire.
          state={isPrintCaptureMode ? { expanded: true } : undefined}
        />
      </Box>
    </>
  )
}

export default HistoryForm
